import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import "../App.css";
import logo from "../assets/logo.png";
import dashboardIcon from "../assets/layout-grid.png";
import appointmentsIcon from "../assets/Tick Square.png";
import logoutIcon from "../assets/logout.png";
import userIcon from "../assets/user_avatar.png";

const EditDoctor = () => {
  const navigate = useNavigate();
  const { doctorId } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    fhirUrl: "",
    hospitalName: "",
    hospitalAddress: "",
    specialty: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Fetch doctor data on component mount
  useEffect(() => {
    const fetchDoctor = async () => {
      try {
        if (doctorId) {
          setLoading(true);
          const doctorRef = doc(db, "doctors", doctorId);
          const doctorSnap = await getDoc(doctorRef);

          if (!doctorSnap.exists()) {
            setError("Doctor not found");
            setLoading(false);
            return;
          }

          const doctorData = doctorSnap.data();
          setFormData({
            name: doctorData.name || "",
            email: doctorData.email || "",
            phone: doctorData.phone || "",
            fhirUrl: doctorData.fhirUrl || "",
            hospitalName: doctorData.hospitalName || "",
            hospitalAddress: doctorData.hospitalAddress || "",
            specialty: doctorData.specialty || "",
          });
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching doctor:", error);
        setError("Failed to load doctor data");
        setLoading(false);
      }
    };

    fetchDoctor();
  }, [doctorId]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("token");
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError("");
    if (success) setSuccess("");
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.hospitalName || !formData.specialty) {
      setError("Name, Email, Phone, Hospital Name, and Specialty are required");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Invalid email address");
      return false;
    }

    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (!phoneRegex.test(formData.phone)) {
      setError("Invalid phone number format");
      return false;
    }

    if (formData.fhirUrl && formData.fhirUrl.trim() !== "") {
      try {
        new URL(formData.fhirUrl);
      } catch (e) {
        setError("Invalid FHIR URL format");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const doctorRef = doc(db, "doctors", doctorId);
      
      // Get existing doctor data to preserve fields we don't update
      const doctorSnap = await getDoc(doctorRef);
      const existingData = doctorSnap.data();

      const doctorData = {
        ...existingData, // Preserve existing data
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        fhirUrl: formData.fhirUrl || null,
        hospitalName: formData.hospitalName,
        hospitalAddress: formData.hospitalAddress || null,
        specialty: formData.specialty,
        updatedAt: new Date().toISOString(),
      };

      await setDoc(doctorRef, doctorData);
      console.log("✅ Doctor document updated");

      setSuccess("Doctor updated successfully!");
      
      // Optionally navigate back after a delay
      setTimeout(() => {
        navigate("/doctors");
      }, 2000);
    } catch (error) {
      console.error("Error updating doctor:", error);
      let errorMessage = "Failed to update doctor. Please try again.";

      if (error.code === "permission-denied") {
        errorMessage = "Permission denied. Please check Firestore security rules.";
      } else if (error.code === "unavailable") {
        errorMessage = "Firestore is unavailable. Please check your connection.";
      } else {
        errorMessage = error.message || "Failed to update doctor. Please try again.";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="appointments-container">
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p>Loading doctor data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="appointments-container">
      {/* Sidebar */}
      <div className="sidebar">
        <img src={logo} alt="Timely Logo" className="logo-image" />
        <div className="user-profile">
          <div className="user-info">
            <h3>Kevin Beers</h3>
            <p>Support Team</p>
          </div>
        </div>
        <nav>
          <a href="/dashboard">
            <img src={dashboardIcon} alt="Dashboard" className="menu-icon" />
            Dashboard
          </a>
          <a href="/appointments">
            <img src={appointmentsIcon} alt="Appointments" className="menu-icon" />
            Appointments
          </a>
          <a href="/users">
            <img src={userIcon} alt="Users" className="menu-icon" />
            Users
          </a>
          <a href="/doctors">
            <span className="menu-icon" style={{ fontSize: "24px" }}>👨‍⚕️</span>
            Doctors
          </a>
          <a href="/" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
            <img src={logoutIcon} alt="Log Out" className="menu-icon" />
            Log Out
          </a>
        </nav>
      </div>

      {/* Main Content */}
      <div className="appointments-page">
        <h1 className="dashboard-title">Edit Doctor</h1>

        {error && (
          <div
            className="error-message"
            style={{
              color: "#d32f2f",
              backgroundColor: "#ffebee",
              padding: "15px",
              borderRadius: "5px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            className="success-message"
            style={{
              color: "#2e7d32",
              backgroundColor: "#e8f5e9",
              padding: "15px",
              borderRadius: "5px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {success}
          </div>
        )}

        <div
          style={{
            backgroundColor: "#ffffff",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
            maxWidth: "600px",
          }}
        >
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Doctor Name *</label>
              <input
                type="text"
                className="login-input"
                name="name"
                placeholder="Dr. John Smith"
                required
                value={formData.name}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Email *</label>
              <input
                type="email"
                className="login-input"
                name="email"
                placeholder="doctor@example.com"
                required
                value={formData.email}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Phone *</label>
              <input
                type="tel"
                className="login-input"
                name="phone"
                placeholder="+1 (555) 123-4567"
                required
                value={formData.phone}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">FHIR URL</label>
              <input
                type="url"
                className="login-input"
                name="fhirUrl"
                placeholder="https://fhir.example.com/Patient/123"
                value={formData.fhirUrl}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Hospital Name *</label>
              <input
                type="text"
                className="login-input"
                name="hospitalName"
                placeholder="General Hospital"
                required
                value={formData.hospitalName}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Hospital Address</label>
              <textarea
                className="login-input"
                name="hospitalAddress"
                placeholder="123 Main St, City, State, ZIP"
                rows="3"
                value={formData.hospitalAddress}
                onChange={handleInputChange}
                disabled={loading}
                style={{ resize: "vertical", minHeight: "80px" }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Specialty *</label>
              <input
                type="text"
                className="login-input"
                name="specialty"
                placeholder="Cardiology, Pediatrics, etc."
                required
                value={formData.specialty}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="submit"
                className="login-btn"
                disabled={loading}
                style={{
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                  flex: 1,
                }}
              >
                {loading ? "Updating..." : "Update Doctor"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/doctors")}
                className="login-btn"
                disabled={loading}
                style={{
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                  backgroundColor: "#6c757d",
                  flex: 1,
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditDoctor;

